/**
 * CR210: Public Deal Retention Policy Card
 * CR315: Extended with visibility stats breakdown
 * 
 * Allows admins to control whether expired deals are shown on the public site
 * and view the current visibility breakdown.
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AlertTriangle, Clock, Save, RotateCcw, Loader2, BarChart3 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { usePublicVisibilityStats } from "@/hooks/usePublicVisibilityStats";
import { VisibilityStatsBreakdown } from "./VisibilityStatsBreakdown";

interface PolicyState {
  mode: "normal" | "keep_expired";
  grace_days: number;
}

interface PublicDealRetentionCardProps {
  initialPolicy?: PolicyState | null;
  onPolicyChange?: (policy: PolicyState) => void;
}

export function PublicDealRetentionCard({ 
  initialPolicy,
  onPolicyChange 
}: PublicDealRetentionCardProps) {
  const [policy, setPolicy] = useState<PolicyState>({
    mode: "normal",
    grace_days: 7,
  });
  const [savedPolicy, setSavedPolicy] = useState<PolicyState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // CR315: Fetch visibility stats
  const { stats: visibilityStats, refetch: refetchStats } = usePublicVisibilityStats();

  // Load initial policy
  useEffect(() => {
    if (initialPolicy) {
      setPolicy(initialPolicy);
      setSavedPolicy(initialPolicy);
    } else {
      fetchPolicy();
    }
    refetchStats();
  }, [initialPolicy]);

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("site-state", {
        method: "GET",
      });

      if (error) throw error;

      const fetchedPolicy: PolicyState = {
        mode: data?.public_deals_policy_mode || "normal",
        grace_days: data?.public_deals_policy_grace_days ?? 7,
      };
      setPolicy(fetchedPolicy);
      setSavedPolicy(fetchedPolicy);
    } catch (err) {
      console.error("[PublicDealRetentionCard] Error fetching policy:", err);
    } finally {
      setLoading(false);
    }
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke("site-state", {
        method: "PUT",
        body: {
          public_deals_policy_mode: policy.mode,
          public_deals_policy_grace_days: policy.grace_days,
        },
      });

      if (error) throw error;

      setSavedPolicy(policy);
      onPolicyChange?.(policy);

      toast({
        title: "Policy Saved",
        description: policy.mode === "keep_expired" 
          ? "Expired deals will now remain visible on the public site"
          : `Normal mode enabled with ${policy.grace_days} day grace window`,
      });
    } catch (err) {
      console.error("[PublicDealRetentionCard] Error saving policy:", err);
      toast({
        title: "Save Failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setPolicy({ mode: "normal", grace_days: 7 });
  };

  const hasChanges = savedPolicy && (
    policy.mode !== savedPolicy.mode || 
    policy.grace_days !== savedPolicy.grace_days
  );

  const isKeepExpiredMode = policy.mode === "keep_expired";

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Public Deal Retention
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isKeepExpiredMode ? "border-warning/50" : undefined}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Public Deal Retention
          {isKeepExpiredMode && (
            <Badge variant="outline" className="ml-2 border-warning text-warning-foreground bg-warning/10">
              Testing Mode
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Controls whether expired deals are shown on the public site (still scoped to active snapshot).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Warning banner when keep_expired is enabled */}
        {isKeepExpiredMode && (
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-warning">
                Testing mode is ON
              </p>
              <p className="text-sm text-warning/80 mt-1">
                Expired deals will remain visible on the public site for testing purposes.
              </p>
            </div>
          </div>
        )}

        {/* Current policy display */}
        <div className="bg-muted/50 rounded-lg p-3">
          <span className="text-sm text-muted-foreground">Current policy: </span>
          <span className="text-sm font-medium">
            {savedPolicy?.mode === "keep_expired" 
              ? "Keep expired" 
              : `Normal · Grace: ${savedPolicy?.grace_days ?? 7} days`}
          </span>
        </div>

        {/* CR315: Visibility Stats Breakdown */}
        {visibilityStats && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <BarChart3 className="h-4 w-4" />
              Current Visibility Breakdown
            </div>
            <VisibilityStatsBreakdown stats={visibilityStats} policy={savedPolicy} compact />
          </div>
        )}

        {/* Mode selection */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Retention Mode</Label>
          <RadioGroup
            value={policy.mode}
            onValueChange={(value) => setPolicy(prev => ({ 
              ...prev, 
              mode: value as "normal" | "keep_expired" 
            }))}
            className="space-y-3"
          >
            <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
              <RadioGroupItem value="normal" id="mode-normal" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="mode-normal" className="font-medium cursor-pointer">
                  Normal (hide after grace window)
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Deals are hidden from the public site after the grace period expires.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 rounded-lg border border-warning/30 hover:bg-warning/5 transition-colors">
              <RadioGroupItem value="keep_expired" id="mode-keep" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="mode-keep" className="font-medium cursor-pointer">
                  Keep expired deals (testing mode)
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Expired deals remain visible for production testing. Only use temporarily.
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Grace days input (only in normal mode) */}
        {policy.mode === "normal" && (
          <div className="space-y-2">
            <Label htmlFor="grace-days" className="text-sm font-medium">
              Grace window (days)
            </Label>
            <div className="flex items-center gap-3">
              <Input
                id="grace-days"
                type="number"
                min={0}
                max={60}
                value={policy.grace_days}
                onChange={(e) => setPolicy(prev => ({ 
                  ...prev, 
                  grace_days: Math.min(60, Math.max(0, parseInt(e.target.value) || 0))
                }))}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">
                Deals remain visible for {policy.grace_days} day{policy.grace_days !== 1 ? "s" : ""} after expiry
              </span>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={savePolicy}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Policy
          </Button>
          <Button
            variant="outline"
            onClick={resetToDefaults}
            disabled={saving || (policy.mode === "normal" && policy.grace_days === 7)}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
